import { HTMLProps, useEffect, useMemo, useRef, useState } from "react";

import { CameraIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/24/solid";
import { createColumnHelper, RowModel, SortingState, Table } from "@tanstack/react-table";

import { ILead, LeadSource } from "../../models/Lead";
import openInNewTab from "../../utils/openInNewTab";
import AwaitingInventoryStatusDropdown from "../awaitingInventory/AwaitingInventoryStatusDropdown";
import YardStatusDropdown from "../awaitingInventory/YardStatusDropdown";
import ContactFromId from "../contacts/ContactFromId";
import BulkCompleteLeads from "../leads/LeadTable/BulkCompleteLeads";
import VehicleIcons from "../shared/VehicleIcons/VehicleIcons";
import CollectedTableSubComponent from "./CollectedTableSubComponent";
import PaymentStatusCell from "../payments/PaymentStatusCell";
import LeadDocumentCell from "../leads/LeadTable/TableSubComponents/LeadDocumentsCell";
import LeadCheckInStatusCell from "../leads/LeadTable/TableSubComponents/LeadCheckInStatusCell";
import useLeadsDocuments from "../../data/useLeadsDocuments";
import PagedTable from "../shared/Tables/PagedTable";
import LeadStatusCell from "../leads/LeadTable/TableSubComponents/LeadStatusCell";
import { DateTime } from "luxon";
import ClickToCopy from "../shared/ClickToCopy";
import VehicleCollectedCODStatus from "./VehicleCollectedCODStatus";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import formatDateTime from "../../utils/formatDateTime";

function IndeterminateCheckbox({
  indeterminate,
  className = '',
  ...rest
}: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
  const ref = useRef<HTMLInputElement>(null!)

  useEffect(() => {
    if (typeof indeterminate === 'boolean') {
      ref.current.indeterminate = !rest.checked && indeterminate
    }
  }, [ref, indeterminate])

  return (
    <input
      type="checkbox"
      ref={ref}
      className={className + 'p-3 cursor-pointer'}
      {...rest}
    />
  )
}

const colHelper = createColumnHelper<ILead>();

export default function VehiclesCollectedTable({ additionalBody }: { additionalBody?: Record<string, any> }) {
  const [actionsOpen, setActionsOpen] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([{ id: "vehicle.collectedOn", desc: true }]);
  const [leads, setLeads] = useState<ILead[]>([]);

  const [table, setTable] = useState<Table<ILead>>();

  const { leadsDocuments } = useLeadsDocuments(leads.map((l) => l.id), "VehiclesCollectedTable");
  const [documents, setDocuments] = useState<any[]>([]);

  useEffect(() => {
    if (leadsDocuments.isLoading) return;
    if (!leadsDocuments.data) return;

    setDocuments(leadsDocuments.data.data);
  }, [leadsDocuments.isLoading, leadsDocuments.data]);


  const cols = useMemo(
    () => [
      TableCheckboxCol(true),
      colHelper.display({
        id: "image",
        cell: (info) => (
          <span className="flex justify-center relative w-32" >
            {info.row.original.vehicle?.vehicleImageUrl ? (
              <>
                <img
                  className="w-32 h-20 object-contain"
                  src={`${info.row.original.vehicle != null ? info.row.original.vehicle.vehicleImageUrl : null}`}
                />
                {info.row.original.vehicle?.vehiclePremium && (
                  <StarIcon className="w-8 h-8 text-amber-500 absolute -top-2 -right-2" />
                )}

              </>
            ) : (
              <CameraIcon className="w-14 text-gray-600" />
            )}
          </span>
        ),
      }),
      colHelper.accessor("vehicle.bookedCollectionOn", {
        header: "Booked On",
        sortingFn: "auto",
        cell: (info) => <span>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT, "Not Set")}</span>,
      }),
      colHelper.accessor("vehicle.collectedOn", {
        id: "vehicle.collectedOn",
        header: "Collected On",
        sortingFn: "auto",
        cell: (info) => <span>{formatDateTime(info.getValue(), DateTime.DATETIME_SHORT, "Not Set")}</span>,
      }),
      colHelper.accessor("vehicle.combinedMake", {
        header: "VRM, Make & Model",
        enableSorting: false,
        cell: (info) => <div>
          {/* <p>{info.row.original.vehicle?.vrm}</p> */}
          <ClickToCopy text={info.row.original.vehicle?.vrm} />
          <p>{info.row.original.vehicle?.combinedMake}</p>
          <p>{info.row.original.vehicle?.combinedModel}</p>
        </div>,
      }),
      colHelper.accessor("contact.fullname", {
        header: "Name & Number",
        sortingFn: "auto",
        cell: (info) => <div>
          <p>{info.getValue()}</p>
          <p>{info.row.original.contact?.telephone1}</p>
        </div>,
      }),
      colHelper.accessor("vehicle.collectedBy", {
        header: "Collected By",
        enableSorting: false,
        cell: (info) => {
          var lead = info.row.original;
          return (lead.vehicle?.collectedBy && lead.driverId && lead.vehicle?.collectedBy !== lead.driverId) ?
            (<div className="flex flex-col">
              <ContactFromId contactId={info.row.original.driverId ?? ""} />
              <span>Marked By: <ContactFromId contactId={info.getValue() ?? ""} /></span>
            </div>)
            :
            (<ContactFromId contactId={info.getValue() ?? ""} />)
        },
      }),
      colHelper.accessor("website", {
        header: "Source",
        enableSorting: false,
        cell: (info) => <span>{LeadSource[info.getValue()]}</span>,
      }),
      colHelper.accessor("vehicle.vehicleStarting", {
        header: "Vehicle",
        enableSorting: false,
        cell: (info) => <div className="w-24"><VehicleIcons vehicle={info.row.original.vehicle!} /></div>,
      }),
      colHelper.accessor("vehicle", {
        header: "COD Status",
        enableSorting: false,
        cell: (info) => <VehicleCollectedCODStatus leadId={info.row.original.id} vehicle={info.getValue()} />
      }),      
      colHelper.accessor("vehicle.checkInId", {
        header: "Check-In",
        id: "checkInStatus",
        enableSorting: false,
        cell: (info) => <LeadCheckInStatusCell checkInId={info.getValue()} />,
      }),
      colHelper.display({
        header: "Status",
        sortingFn: "auto",
        cell: (info) => (
          <div className="flex flex-col items-start justify-center space-y-4">
            <PaymentStatusCell lead={info.row.original} />
            <LeadStatusCell status={info.row.original.status} />
          </div>
        ),
      }),
      colHelper.display({
        header: "Images",
        sortingFn: "auto",
        cell: (info) => <LeadDocumentCell loading={leadsDocuments.isLoading} documents={documents.filter(d => d.leadId == info.row.original.id)} />,
      }),
      colHelper.accessor("vehicle.yardStatus", {
        header: "Status",
        enableSorting: false,
        cell: (info) => <div id={info.row.original.id}>
          <p>Parts Status</p>
          <AwaitingInventoryStatusDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
          <p className="mt-2">Yard Status</p>
          <YardStatusDropdown vehicle={info.row.original.vehicle!} classes="h-8 w-40 text-sm" />
        </div>
      }),
    ],
    [leadsDocuments.isLoading, documents]
  );


  return (
    <>
      <BulkCompleteLeads table={table} open={actionsOpen} setOpen={setActionsOpen} />

      <PagedTable
        columns={cols}
        url="/lead/partialwithfilters"
        pageMode={undefined}
        queryLabel="Search Name, VRM or Postcode"
        expandedRow={(lead: ILead) => <CollectedTableSubComponent lead={lead} />}
        additionalBody={{
          ...additionalBody
        }}
        onRowClick={(x: ILead) => openInNewTab(`leads/${x.id}`)}
        initialSort={[{ id: "vehicle.collectedOn", desc: true }]}
        setRowData={setLeads}
        actionButtons={[
          {
            label: "Bulk Actions",
            onClick: (rows: RowModel<ILead>, table: Table<ILead>) => {
              setTable(table);
              setActionsOpen(true);
            },
            disabledOnNoRows: true,
            isLoading: false,
          },
        ]}
      />
    </>
  );

}
